import { ReactNode, createContext, useContext, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { FullScreenProgress, Typography } from '@occc-app/design-system';
import {
  CurrentUserRetailerNetworkUserFragment,
  Retailer_Network_Users_Sort_By,
  Retailer_Network_Users_Sort_Order,
} from '@occc/graphql';
import { useGetCurrentUserRetailerNetworkUsers } from '@occc-app/dataAccessLayer/openCommerceUser/queries';

import { errorMessage } from '../retailer-connect.copies';

type RetailerNetworkContextValue = {
  retailerNetworkUser: CurrentUserRetailerNetworkUserFragment | null;
};

const RetailerNetworkContext = createContext<RetailerNetworkContextValue>({
  retailerNetworkUser: null,
});

export const RetailerNetworkContextProvider = ({ children }: { children?: ReactNode }) => {
  const { networkId } = useParams<{ networkId: string }>();
  const retailerNetworkUuid = networkId ?? null;

  const { data, loading, error } = useGetCurrentUserRetailerNetworkUsers({
    pageSize: 1,
    retailerNetworkUuid: retailerNetworkUuid ?? undefined,
    sort: {
      direction: Retailer_Network_Users_Sort_Order.Desc,
      field: Retailer_Network_Users_Sort_By.Role,
    },
    skip: !retailerNetworkUuid,
  });

  const retailerNetworkUser = data?.[0] ?? null;
  const value = useMemo(() => ({ retailerNetworkUser }), [retailerNetworkUser]);

  if (loading) {
    return <FullScreenProgress />;
  }

  if (error) {
    return (
      <Typography color='error' align='center'>
        {errorMessage}
      </Typography>
    );
  }

  if (!loading && !retailerNetworkUuid) {
    return null;
  }

  return (
    <RetailerNetworkContext.Provider value={value}>
      {children || <Outlet />}
    </RetailerNetworkContext.Provider>
  );
};

export const useRetailerNetworkContext = () => useContext(RetailerNetworkContext);
